import { DialogContentProps } from "@radix-ui/react-dialog";
import { Area, CropperProps } from "react-easy-crop";
import * as DialogPrimitive from "@radix-ui/react-dialog";

export interface CroppedImagePayload {
  crop: { x: number; y: number };
  croppedAreaPixels: Area | null;
}

export interface CroppedImagePayloadWithBlobUrl extends CroppedImagePayload {
  croppedImageBlobUrl: string;
}

interface CropperModalBaseProps {
  open?: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel?: () => void;
  onError?: (e: CropperModalError | unknown) => void;
  imageSrc?: string | null;
  isSVG?: boolean;
  cropSize?: { width: number; height: number };
  /**
   * @property {boolean} outputExactCropSize - Determine whether to ouput image with the exact size given in cropSize attribute
   * @description an original image dimension is 1000x1000px, if cropSize is 500x500px, the output image will be exactly 500x500px
   */
  outputExactCropSize?: boolean;
  targetFileSizeKB?: number;
  title?: string;
  props?: {
    dialogTitleContainer?: React.ComponentProps<"div">;
    dialogTitle?: React.ComponentProps<typeof DialogPrimitive.Title>;
    dialogContent?: DialogContentProps;
    cropper?: CropperProps;
    cropperContainer?: React.ComponentProps<"div">;
  };
}

interface CropperModalWithBlobUrlOutputProps extends CropperModalBaseProps {
  onConfirm?: (payload: CroppedImagePayloadWithBlobUrl) => void;
  generateBlobUrlOutput: true;
}

interface CropperModalNormalProps extends CropperModalBaseProps {
  onConfirm?: (payload: CroppedImagePayload) => void;
  generateBlobUrlOutput: undefined | false;
}

export type CropperModalProps = CropperModalWithBlobUrlOutputProps | CropperModalNormalProps;

export type CropperModalErrorType =
  | "ERROR_GENERATE_BLOB"
  | "ERROR_CREATE_CANVAS"
  | "ERROR_INVALID_SVG"
  | "ERROR_COMPRESSION"
  | "ERROR_SVG_EXCEED_SIZE";

export class CropperModalError extends Error {
  public readonly error: CropperModalErrorType;
  constructor(errorType: CropperModalErrorType, message: string) {
    super(message);
    this.error = errorType;
  }
}

export const ErrorGeneratingBlob = new CropperModalError(
  "ERROR_GENERATE_BLOB",
  "failed to generate a valid Blob"
);

export const ErrorCreateCanvas = new CropperModalError("ERROR_CREATE_CANVAS", "Failed to create canvas");

export const ErrorInvalidSVG = new CropperModalError(
  "ERROR_INVALID_SVG",
  "The file is not a valid SVG document root"
);

export const ErrorCompression = new CropperModalError(
  "ERROR_COMPRESSION",
  "Compression failed to generate a valid Blob"
);

export const ErrorSVGExceedSize = new CropperModalError(
  "ERROR_SVG_EXCEED_SIZE",
  "The SVG file is too large"
);
